import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { ExternalLink, X } from "lucide-react";
import { pushModal } from "../../../lib/modalStack";

interface Props {
  url: string | null;
  label: string;
  onClose: () => void;
  open: boolean;
}

/**
 * In-app viewer for a non-image attachment (PDFs and the like), with its own Close button —
 * unlike a plain `<a target="_blank">`, which is what this replaces.
 *
 * That plain link is exactly right in an ordinary browser tab, which always has its own back
 * button and address bar. It is a dead end inside an **installed PWA**: a standalone window has no
 * browser chrome at all, and a same-origin navigation (which `/static/…` and the API's attachment
 * routes both are) does not open a new tab there — it replaces the app in place, with nothing on
 * screen to get back with. `AppointmentAttachments` only reaches for this overlay once
 * `isStandalone()` is true; an ordinary tab keeps the simpler `target="_blank"` link.
 *
 * The `iframe` is the pragmatic middle ground: an in-app PDF preview cannot be guaranteed to
 * render on every engine (iOS Safari's is unreliable), but failing quietly here still leaves the
 * one thing that actually matters — the Close button — on screen throughout, plus an explicit
 * "Öffnen" escape hatch for when the embed does not render.
 */
export const DocumentViewer: React.FC<Props> = ({ url, label, onClose, open }) => {
  const panelRef = useRef<HTMLDivElement>(null);

  const closeRef = useRef(onClose);
  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    return pushModal({
      onEscape: () => closeRef.current(),
      panel: () => panelRef.current,
      trapFocus: true,
    });
  }, [open]);

  // Focus the panel on open, return focus to whatever opened it on close — same contract as Modal
  // and Lightbox.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    panelRef.current?.focus({ preventScroll: true });
    return () => {
      if (previous && previous.isConnected) previous.focus({ preventScroll: true });
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open || !url) return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="true"
      aria-label={label}
      tabIndex={-1}
      className="fixed inset-0 z-[200] flex flex-col bg-white outline-none"
    >
      <div
        className="flex items-center gap-1 border-b border-slate-200 px-1"
        style={{
          paddingTop: "max(0.5rem, env(safe-area-inset-top))",
          paddingLeft: "max(0.5rem, env(safe-area-inset-left))",
          paddingRight: "max(0.5rem, env(safe-area-inset-right))",
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-700 hover:bg-slate-100"
        >
          <X size={22} aria-hidden="true" />
        </button>
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-slate-900">{label}</span>
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="flex h-11 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-primary hover:bg-slate-100"
        >
          <ExternalLink size={16} aria-hidden="true" />
          Öffnen
        </a>
      </div>

      <iframe src={url} title={label} className="flex-1 border-0 bg-white" />
    </div>,
    document.body,
  );
};
